import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  DocSignService,
  EgibObject,
  FormAlertService,
  MapAction,
  MapId,
  MapObject,
  MapObjectApiType,
  MapObjectTableActionType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  PortalId,
  PzService,
  StreetService,
  ToolType,
  markFormGroupsTouched,
} from '@gk/gk-modules';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  catchError,
  from,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { ProxyDetailsComponent } from '../../../components/proxy-details/proxy-details.component';
import { RequestInProgress } from '../../../guards/request-in-progress/request-in-progress.guard';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../services/place/place.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { InvestorDetailsComponent } from '../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import {
  HSTNNewRequestControlName,
  HSTNNewRequestFormGroup,
} from '../services/new-request-form/new-request-form.model';
import { HSTNNewRequestFormService } from '../services/new-request-form/new-request-form.service';
import {
  HSTNNewRequest,
  HSTNNewRequestDto,
  HSTNNewRequestFormWorkspaceState,
} from '../services/new-request/new-request.model';
import { MainRoutes } from './../../../guards/guard.models';

@Component({
  selector: 'app-lbrc-new-request',
  templateUrl: './new-request.component.html',
  standalone: false,
})
export class HSTNNewRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy, RequestInProgress
{
  override controlName = HSTNNewRequestControlName;
  override formGroup: FormGroup<HSTNNewRequestFormGroup>;
  override portalId = PortalId.HouseholdSewageTreatmentNotification;
  readonly mapObjectTableState = new MapObjectTableState(
    [
      new ColumnHeader('districtName', 'GK.MAP.DISTRICT'),
      new ColumnHeader('mapObjectNumber', 'PARCEL_SEARCH_FORM.PARCEL_NUMBER'),
      new ColumnHeader('area', 'GK.MAP.AREA_IN_HA'),
    ],
    undefined,
    this.defaultMapGeometryStyles,
    true,
    false,
    true,
    '25',
  );
  mapSettings: MapSettings;
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild(ProxyDetailsComponent)
  proxyDetailsComponent: ProxyDetailsComponent;
  @ViewChild('canDeactivateModal') canDeactivateModal: NgbModalRef;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  successSigned = false;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.HouseholdSewageTreatmentNotificationFormVersion;
  workspaceStateId = WorkspaceStateId.HouseholdSewageTreatmentNotificationForm;
  otherFileRequired = false;

  constructor(
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    private hstnNewRequestFormService: HSTNNewRequestFormService,
    public override streetService: StreetService,
    private toastr: ToastrService,
    public override router: Router,
    private modalService: NgbModal,
    public override pzService: PzService,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
    );
  }

  override ngOnInit(): void {
    this.fetchWrongFileText();
    this.subscribeToDocSignTranslations();
    this.createForm();
    this.fetchMapSettings();
  }

  createForm(): void {
    this.formGroup = this.hstnNewRequestFormService.getFormGroup();
  }

  fetchMapSettings(): void {
    this.mapSettingsService
      .getMapSettings(MapPortalName.Applicant)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((mapSettings) => {
        this.mapSettings = mapSettings;
        this.setMapState();
      });
  }

  override setMapState(): void {
    this.mapState = new MapState(
      MapId.HouseholdSewageTreatmentNotification,
      this.mapStateService.getViewState(
        MapId.HouseholdSewageTreatmentNotification,
        this.mapSettings,
      ),
      this.mapStateService.getToolbarState(
        [ToolType.LandParcel],
        this.mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.HouseholdSewageTreatmentNotification,
        this.mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.isRequestDirty() && !this.successSigned
      ? from(
          this.modalService.open(this.canDeactivateModal, {
            beforeDismiss: () => false,
          }).result,
        )
      : true;
  }

  isRequestDirty(): boolean {
    return (
      this.formGroup.dirty || this.attachmentsComponent.areChosenDocuments()
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.attachmentsComponent.areDocumentsValid() &&
      this.isMapSectionValid()
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    markFormGroupsTouched(this.getInvestorDetailsFormGroup());
    if (!this.isRequestValid()) {
      return;
    }

    this.sendRequest();
  }

  sendRequest(): void {
    this.getRequestBody()
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap((body) =>
          this.docSignService.addToSign(
            body,
            '/api/interesant/wniosek/zgloszenieEksploatacjiPrzydomowejOczyszczalniSciekow/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: (error) => this.handleSendAndValidateFailure(error),
      });
  }

  getRequestBody = (): Observable<HSTNNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.errorSubmitMessage = this.wrongFilesErrorText;
          this.toastr.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        HSTNNewRequest.fromAppToApi(
          this.getFormValue(),
          this.getLandParcels(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave = (): HSTNNewRequestFormWorkspaceState => ({
    formValue: this.getFormValue(),
    parcels: this.getLandParcels(),
  });

  handleLoadWorkspaceResponse(state: HSTNNewRequestFormWorkspaceState): void {
    this.formGroup.patchValue({
      ...state.formValue,
      investorDetails: {
        ...state.formValue.investorDetails,
        addressSelect: undefined,
      },
    });
    if (state.formValue?.investorDetails?.addressSelect) {
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        state.formValue.investorDetails.addressSelect,
      );
    }
    if (state.formValue?.proxyDetailsArray) {
      this.proxyDetailsComponent.setFormGroupValue(
        state.formValue.proxyDetailsArray,
      );
    }
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: state.formValue.documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    if (state.parcels) {
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.AddNew, [...state.parcels]),
      );
    }
  }

  getLandParcels(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
      ),
    );
  }

  getFormValue(): HSTNNewRequest {
    return this.formGroup.getRawValue();
  }

  override handleDocSignSuccess(): void {
    this.successSigned = true;
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.HouseholdSewageTreatmentNotification}/requests-list`,
    );
  }

  isMapSectionValid(): boolean {
    return this.areLandParcelsChosen();
  }

  areLandParcelsChosen(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.LandParcel,
    );
  }

  isActivityDescriptionFormControlValid(): boolean {
    return this.getActivityDescriptionFormControl().valid;
  }

  getActivityDescriptionFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.ActivityDescription,
    ) as FormControl;
  }

  isInstallationOperatingTimeFormControlValid(): boolean {
    return this.getInstallationOperatingTimeFormControl().valid;
  }

  getInstallationOperatingTimeFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.InstallationOperatingTime,
    ) as FormControl;
  }

  isEmissionTypeAndAmountFormControlValid(): boolean {
    return this.getEmissionTypeAndAmountFormControl().valid;
  }

  getEmissionTypeAndAmountFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.EmissionTypeAndAmount,
    ) as FormControl;
  }

  isEmissionReductionMethodsFormControlValid(): boolean {
    return this.getEmissionReductionMethodsFormControl().valid;
  }

  getEmissionReductionMethodsFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.EmissionReductionMethods,
    ) as FormControl;
  }

  isReductionCompliantDescriptionFormControlValid(): boolean {
    return this.getReductionCompliantDescriptionFormControl().valid;
  }

  getReductionCompliantDescriptionFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.ReductionCompliantDescription,
    ) as FormControl;
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
